/**
 * PackedCards — the My Work windows, packed like a shelf, not a grid.
 *
 * The cards used to sit in a CSS grid, where every row is as tall as
 * its tallest card. One client with eight items (David Cummings, 9/10)
 * made the whole first row eight items tall, so Harold Burke's
 * one-item card sat alone on a second row under a desert of white
 * space. Matthew: "Harold Burke needs to move up a bit — the cards
 * need to be a bit better organized."
 *
 * So: measure the container, decide how many columns fit (same 430px
 * minimum the grid used), and deal each card to the currently
 * shortest column, in the order the plate handed them over. Height is
 * estimated from row count (header + rows) rather than measured, so
 * the layout is stable — expanding a card in place only pushes the
 * cards beneath it in its own column, and never reshuffles the rest.
 * Ties go to the leftmost column, so a plate of equal cards still
 * reads left-to-right, top-to-bottom like before.
 */

import React, { useLayoutEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { spacing } from "../../../theme/tokens";

const MIN_COLUMN_WIDTH = 430;
const GAP_PX = 12; // spacing[3]

/**
 * Deal `items` (each `{ key, weight, node }`) into `columnCount`
 * columns, shortest column first, ties to the left. Pure; exported for
 * tests.
 */
export function packIntoColumns(items, columnCount) {
  const count = Math.max(1, columnCount | 0);
  const columns = Array.from({ length: count }, () => []);
  const heights = new Array(count).fill(0);
  items.forEach((item) => {
    let target = 0;
    for (let i = 1; i < count; i += 1) {
      if (heights[i] < heights[target]) target = i;
    }
    columns[target].push(item);
    heights[target] += Math.max(1, item.weight || 1);
  });
  return columns;
}

/** How many `MIN_COLUMN_WIDTH` columns fit in `width`, gap included. */
export function columnsFor(width) {
  if (!width || width <= 0) return 1;
  return Math.max(
    1,
    Math.floor((width + GAP_PX) / (MIN_COLUMN_WIDTH + GAP_PX))
  );
}

const Shelf = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${spacing[3]};
  padding-bottom: ${spacing[6]};
`;

const Column = styled.div`
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: ${spacing[3]};
`;

const PackedCards = ({ items }) => {
  const ref = useRef(null);
  // Measured from the shelf itself, before the first paint — the
  // viewport is wider than the shelf (sidebar, page padding), so
  // seeding from it would open too many columns and then jump.
  const [columnCount, setColumnCount] = useState(1);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const measure = () => setColumnCount(columnsFor(el.clientWidth));
    measure();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const columns = packIntoColumns(items, columnCount);

  return (
    <Shelf ref={ref} data-testid="packed-cards" data-columns={columns.length}>
      {columns.map((column, i) => (
        <Column key={i} data-testid={`packed-column-${i}`}>
          {column.map((item) => (
            <React.Fragment key={item.key}>{item.node}</React.Fragment>
          ))}
        </Column>
      ))}
    </Shelf>
  );
};

PackedCards.propTypes = {
  /** Cards in plate order. `weight` is the estimated height in rows
   * (header + rows); `node` is the rendered card. */
  items: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
      weight: PropTypes.number,
      node: PropTypes.node.isRequired,
    })
  ).isRequired,
};

export default PackedCards;
